import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query'
import { apiClient } from '@/shared/services/client'
import { useTeam } from '@/shared/context/TeamContext'
import type { Campania, CampaniaFilters, FiltersData, DashboardStats, CampaniaDetalle, CampaniaUpdateData, Reporte, ProgresoLaboratorio, Categorizacion, CampaniaHtml } from '@/modules/multichannel/types/campania'
import type { CampaniaCreateInput } from '../schemas/campania-create'

// Fetch campanias con filtros opcionales
export function useCampanias(filters?: CampaniaFilters, page: number = 1) {
  const { selectedArea } = useTeam()
  return useQuery({
    queryKey: ['campanias', selectedArea?.id, filters, page],
    queryFn: async () => {
      const params = new URLSearchParams()

      // Ordenamiento (siempre aplicar, default ascendente por fecha)
      params.append('orden', filters?.orden || 'fecha_publicacion')

      if (filters?.search) params.append('search', filters.search)
      if (filters?.marca_principal_id) params.append('marca_principal_id', filters.marca_principal_id.toString())
      if (filters?.marca_secundaria_id) params.append('marca_secundaria_id', filters.marca_secundaria_id.toString())
      if (filters?.estado_envio) params.append('estado_envio', filters.estado_envio)
      if (filters?.estado_contenido) params.append('estado_contenido', filters.estado_contenido)
      if (filters?.estado_reporte) params.append('estado_reporte', filters.estado_reporte)
      if (filters?.tipo) params.append('tipo', filters.tipo)
      if (filters?.formato) params.append('formato', filters.formato)
      if (filters?.nombres_campania?.length) params.append('nombres_campania', filters.nombres_campania.join(','))
      if (filters?.asesor_id) params.append('asesor_id', filters.asesor_id.toString())
      if (filters?.fecha_desde) params.append('fecha_desde', filters.fecha_desde)
      if (filters?.fecha_hasta) params.append('fecha_hasta', filters.fecha_hasta)

      params.append('page', page.toString())

      const response = await apiClient.get<{ items: Campania[], count: number }>(
        `/multichannel/campanias/?${params.toString()}`
      )

      return response.data
    },
    placeholderData: keepPreviousData,
  })
}

// Fetch TODAS las campañas que coincidan con los filtros (recorre todas las páginas).
// Útil para vistas que calculan KPIs y filtran en el cliente (ej. Analytics de Campañas),
// replicando el comportamiento del legacy que carga todo en una tabla DataTables.
export function useCampaniasEnviadas(filters?: CampaniaFilters) {
  const { selectedArea } = useTeam()
  return useQuery({
    queryKey: ['campanias-enviadas', selectedArea?.id, filters],
    queryFn: async () => {
      const buildQuery = (page: number) => {
        const params = new URLSearchParams()
        params.append('orden', filters?.orden || '-fecha_publicacion')
        if (filters?.search) params.append('search', filters.search)
        if (filters?.marca_principal_id) params.append('marca_principal_id', filters.marca_principal_id.toString())
        if (filters?.estado_envio) params.append('estado_envio', filters.estado_envio)
        if (filters?.estado_reporte) params.append('estado_reporte', filters.estado_reporte)
        if (filters?.tipo) params.append('tipo', filters.tipo)
        if (filters?.fecha_desde) params.append('fecha_desde', filters.fecha_desde)
        if (filters?.fecha_hasta) params.append('fecha_hasta', filters.fecha_hasta)
        params.append('page', page.toString())
        return params.toString()
      }

      const first = await apiClient.get<{ items: Campania[]; count: number }>(
        `/multichannel/campanias/?${buildQuery(1)}`
      )
      const count = first.data.count
      const PAGE_SIZE = 25 // page_size fijo del backend (@paginate PageNumberPagination)
      const totalPages = Math.ceil(count / PAGE_SIZE)

      let items = first.data.items
      if (totalPages > 1) {
        const restPages = Array.from({ length: totalPages - 1 }, (_, i) => i + 2)
        const responses = await Promise.all(
          restPages.map((p) =>
            apiClient.get<{ items: Campania[]; count: number }>(`/multichannel/campanias/?${buildQuery(p)}`)
          )
        )
        items = items.concat(...responses.map((r) => r.data.items))
      }
      return items
    },
    placeholderData: keepPreviousData,
  })
}

// Fetch opciones de filtros (contextualizadas por filtros activos)
export function useFiltersData(filters?: CampaniaFilters) {
  const { selectedArea } = useTeam()
  return useQuery({
    queryKey: ['multichannel-filters', selectedArea?.id, filters],
    queryFn: async () => {
      const params = new URLSearchParams()
      if (filters?.marca_principal_id) params.append('marca_principal_id', filters.marca_principal_id.toString())
      if (filters?.estado_envio) params.append('estado_envio', filters.estado_envio)
      if (filters?.estado_contenido) params.append('estado_contenido', filters.estado_contenido)
      if (filters?.estado_reporte) params.append('estado_reporte', filters.estado_reporte)
      if (filters?.tipo) params.append('tipo', filters.tipo)
      if (filters?.fecha_desde) params.append('fecha_desde', filters.fecha_desde)
      if (filters?.fecha_hasta) params.append('fecha_hasta', filters.fecha_hasta)
      if (filters?.nombres_campania?.length) params.append('nombres_campania', filters.nombres_campania.join(','))
      const qs = params.toString()
      const response = await apiClient.get<FiltersData>(`/multichannel/filters/${qs ? `?${qs}` : ''}`)
      return response.data
    },
    staleTime: 30 * 1000, // Cache por 30 segundos (se refetch al cambiar filtros)
    placeholderData: keepPreviousData,
  })
}

// Fetch estadísticas del dashboard
export function useDashboardStats() {
  const { selectedArea } = useTeam()
  return useQuery({
    queryKey: ['multichannel-stats', selectedArea?.id],
    queryFn: async () => {
      const response = await apiClient.get<DashboardStats>('/multichannel/stats/dashboard/')
      return response.data
    },
    refetchInterval: 30000, // Refetch cada 30 segundos
  })
}

// Fetch una campaña específica
export function useCampania(id: number) {
  return useQuery({
    queryKey: ['campania', id],
    queryFn: async () => {
      const response = await apiClient.get<CampaniaDetalle>(`/multichannel/campanias/${id}/`)
      return response.data
    },
    enabled: !!id,
  })
}

// Actualizar una campaña
export function useUpdateCampania() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: CampaniaUpdateData }) => {
      const response = await apiClient.patch<CampaniaDetalle>(
        `/multichannel/campanias/${id}/`,
        data
      )
      return response.data
    },
    onSuccess: (data) => {
      // Invalidar queries relacionadas
      queryClient.invalidateQueries({ queryKey: ['campania', data.id] })
      queryClient.invalidateQueries({ queryKey: ['campanias'] })
    },
  })
}

// Fetch un reporte específico
export function useReporte(id: number) {
  return useQuery({
    queryKey: ['reporte', id],
    queryFn: async () => {
      const response = await apiClient.get<Reporte>(`/multichannel/reportes/${id}/`)
      return response.data
    },
    enabled: !!id,
  })
}

// Fetch reporte por campaña ID (usa endpoint que sigue redirecciones)
export function useReporteByCampania(campaniaId: number, enabled: boolean = false) {
  return useQuery({
    queryKey: ['reporte-campania', campaniaId],
    queryFn: async () => {
      try {
        const response = await apiClient.get<Reporte>(`/multichannel/reportes/por-campania/${campaniaId}/`)
        return response.data
      } catch (error: any) {
        if (error?.response?.status === 404) return null
        throw error
      }
    },
    enabled: enabled && !!campaniaId,
    retry: false,
  })
}

// Duplicar una campaña
export function useDuplicateCampania() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: number) => {
      const response = await apiClient.post<CampaniaDetalle>(
        `/multichannel/campanias/${id}/duplicar/`
      )
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campanias'] })
      queryClient.invalidateQueries({ queryKey: ['multichannel-stats'] })
    },
  })
}

// Crear una nueva campaña
export function useCreateCampania() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: CampaniaCreateInput) => {
      const response = await apiClient.post<CampaniaDetalle>(
        '/multichannel/campanias/',
        data
      )
      return response.data
    },
    onSuccess: () => {
      // Invalidar queries relacionadas
      queryClient.invalidateQueries({ queryKey: ['campanias'] })
      queryClient.invalidateQueries({ queryKey: ['multichannel-stats'] })
    },
  })
}

// Fetch progreso por laboratorio
export function useProgresoLaboratorios(filters?: CampaniaFilters) {
  const { selectedArea } = useTeam()
  return useQuery({
    queryKey: ['multichannel-progreso-labs', selectedArea?.id, filters],
    queryFn: async () => {
      const params = new URLSearchParams()
      if (filters?.marca_principal_id) params.append('marca_principal_id', filters.marca_principal_id.toString())
      if (filters?.estado_envio) params.append('estado_envio', filters.estado_envio)
      if (filters?.estado_contenido) params.append('estado_contenido', filters.estado_contenido)
      if (filters?.estado_reporte) params.append('estado_reporte', filters.estado_reporte)
      if (filters?.tipo) params.append('tipo', filters.tipo)
      if (filters?.formato) params.append('formato', filters.formato)
      if (filters?.asesor_id) params.append('asesor_id', filters.asesor_id.toString())
      if (filters?.fecha_desde) params.append('fecha_desde', filters.fecha_desde)
      if (filters?.fecha_hasta) params.append('fecha_hasta', filters.fecha_hasta)

      const response = await apiClient.get<ProgresoLaboratorio[]>(
        `/multichannel/stats/progreso-laboratorios/?${params.toString()}`
      )
      return response.data
    },
  })
}

// Fetch campañas pendientes de envío
export function usePendientesCampanias(filters?: { fecha_desde?: string; fecha_hasta?: string }) {
  const { selectedArea } = useTeam()
  return useQuery({
    queryKey: ['multichannel-pendientes', selectedArea?.id, filters],
    queryFn: async () => {
      const params = new URLSearchParams()
      if (filters?.fecha_desde) params.append('fecha_desde', filters.fecha_desde)
      if (filters?.fecha_hasta) params.append('fecha_hasta', filters.fecha_hasta)
      const query = params.toString()
      const response = await apiClient.get<Campania[]>(
        `/multichannel/stats/pendientes/${query ? `?${query}` : ''}`
      )
      return response.data
    },
  })
}

// Fetch categorización de campañas
export function useCategorizacion(campaniaIds: number[]) {
  return useQuery({
    queryKey: ['multichannel-categorizacion', campaniaIds],
    queryFn: async () => {
      if (campaniaIds.length === 0) return {}
      const response = await apiClient.get<Record<string, Categorizacion>>(
        `/multichannel/stats/categorizacion/?campania_ids=${campaniaIds.join(',')}`
      )
      return response.data
    },
    enabled: campaniaIds.length > 0,
  })
}

// Fetch HTML de campaña para preview
export function useCampaniaHtml(campaniaId: number | null) {
  return useQuery({
    queryKey: ['campania-html', campaniaId],
    queryFn: async () => {
      const response = await apiClient.get<CampaniaHtml>(
        `/multichannel/campanias/${campaniaId}/html/`
      )
      return response.data
    },
    enabled: !!campaniaId,
  })
}
